import { useState, useEffect } from "react";
import { onValue, ref } from "firebase/database";
import {
  Card,
  CardContent,
  Typography,
  ButtonGroup,
  Button,
  Box,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import CircularProgress from "@mui/material/CircularProgress";

import { database } from "../firebase";
import TotalsChart from "../components/craft-stats/TotalsChart";
import CraftChart from "../components/craft-stats/CraftChart";
import DeckChart from "../components/craft-stats/DeckChart";
import { crafts } from "../utils";
import { ROTATION } from "../constants";
import FormatButtons from "../components/format-buttons/FormatButtons";
import SeasonSelect from "../components/season-select/SeasonSelect";
import seasons from "../seasons";
import { useUser } from "../contexts/UserContext";

// This is a v4 legacy use. I'm using it because non-mui components cannot access the sx property
// Check back here for updates to change later https://mui.com/system/basics/#4-any-element-with-the-babel-plugin
const useStyles = makeStyles({
  formatIconLeft: {
    width: "1.5em",
    marginRight: 10
  },
  formatIconRight: {
    width: "1.5em",
    marginLeft: 10
  }
});

const currentSeason = Date.now();

const StatsPage = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const user = useUser();

  const [pageLoad, setPageLoad] = useState(true);
  const [myStats, setMyStats] = useState(true);
  const [format, setFormat] = useState(ROTATION);
  const [sortedGames, setSortedGames] = useState(null);
  const [craftData, setCraftData] = useState(null);
  const [deckData, setDeckData] = useState(null);
  const [season, setSeason] = useState(
    seasons.filter((s) => s.start < currentSeason && s.end > currentSeason)[0]
  );

  const sortCrafts = (games) => {
    const gameAcc = {};
    crafts.forEach((craft) => {
      const r = new RegExp(craft, "i");
      const result = games.filter((game) => r.test(game.deck));
      gameAcc[craft] = result;
    });
    return gameAcc;
  };

  const sortSeasons = (games) => {
    if (!season) return games;
    const gamesInSeason = games.filter(
      (game) => game.id > season.start && game.id < season.end
    );
    return gamesInSeason;
  };

  const sortFormat = (games) => games.filter((game) => game.format === format);

  const fetchMyGames = () => {
    setPageLoad(true);
    const dbRef = ref(database, `id/${user.uid}/games`);
    onValue(dbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const myGames = Object.values(data);
        const seasonGames = sortSeasons(myGames);
        const formatGames = sortFormat(seasonGames);
        setSortedGames(sortCrafts(formatGames));
      } else {
        setSortedGames(null);
      }
    });
  };

  const fetchAllGames = () => {
    if (user) {
      setPageLoad(true);
      const dbRef = ref(database, `id`);
      onValue(dbRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const arrayData = Object.values(data);
          const allGames = arrayData.reduce(
            (acc, curr) => [...acc, ...Object.values(curr.games)],
            []
          );
          const seasonGames = sortSeasons(allGames);
          const formatGames = sortFormat(seasonGames);
          const sortGames = sortCrafts(formatGames);

          setSortedGames(sortGames);
        } else {
          setSortedGames(null);
        }
      });
    }
  };

  const fetchGames = () => (myStats ? fetchMyGames() : fetchAllGames());

  const countWins = (games) =>
    games.filter((game) => game.result === "WIN").length;
  const countLosses = (games) => games.length - countWins(games);
  const getTotalsData = (games) => {
    const formattedData = crafts.map((craft) => ({
      name: craft.toUpperCase(),
      wins: countWins(games[craft]),
      losses: countLosses(games[craft])
    }));
    return formattedData;
  };

  useEffect(() => {
    if (user) fetchGames();
  }, [user, myStats, format, season]);

  useEffect(() => {
    setPageLoad(false);
  }, [sortedGames]);

  useEffect(() => {
    setCraftData(null);
    setDeckData(null);
  }, [myStats, format]);

  useEffect(() => {
    setDeckData(null);
  }, [craftData, format]);

  const getCraftData = (craft) => {
    const usageData = {};
    sortedGames[craft.toLowerCase()].forEach((val) => {
      if (!Object.hasOwn(usageData, val.deck)) {
        usageData[val.deck] = {
          craft,
          name: val.deck,
          value: 0,
          wins: 0,
          losses: 0
        };
      }
      if (val.result === "WIN") usageData[val.deck].wins++;
      if (val.result === "LOSE") usageData[val.deck].losses++;
      usageData[val.deck].value++;
    });
    setCraftData(Object.values(usageData));
  };

  const getDeckData = (craft, deckName) => {
    const gameAcc = {};

    const r = new RegExp(deckName);
    const result = sortedGames[craft].filter((game) => r.test(game.deck));
    gameAcc[deckName] = result;

    setDeckData(Object.values(gameAcc)[0]);
  };

  const handleFormatClick = (formatValue) => setFormat(formatValue);
  const handleSeasonChange = (seasonId) => {
    const chosenSeason = seasons.filter((s) => s.id === seasonId);
    setSeason(chosenSeason[0]);
  };

  return (
    <>
      <Card sx={{ my: 3 }}>
        <CardContent>
          <Typography variant="h5">Game Stats</Typography>
          {pageLoad ? (
            <CircularProgress sx={{ marginTop: 10, marginBottom: 10 }} />
          ) : (
            sortedGames && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {Object.values(sortedGames).reduce(
                    (acc, curr) => acc + curr.length,
                    0
                  )}{" "}
                  Games
                </Typography>
                <TotalsChart
                  chartData={getTotalsData(sortedGames)}
                  onBarClick={getCraftData}
                />
              </>
            )
          )}

          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            justifyContent="space-evenly"
            alignItems="center"
            mt={2}
          >
            <ButtonGroup fullWidth={isSmallScreen}>
              <Button
                variant={myStats ? "contained" : "outlined"}
                onClick={() => setMyStats(true)}
              >
                <PersonIcon className={classes.formatIconLeft} />
                My Stats
              </Button>
              <Button
                variant={myStats ? "outlined" : "contained"}
                onClick={() => setMyStats(false)}
              >
                The Meta
                <GroupsIcon className={classes.formatIconRight} />
              </Button>
            </ButtonGroup>

            <FormatButtons format={format} onFormatClick={handleFormatClick} />

            <SeasonSelect season={season} onSeasonChange={handleSeasonChange} />
          </Box>
        </CardContent>
      </Card>

      {craftData && (
        <CraftChart chartData={craftData} onDeckClick={getDeckData} />
      )}
      {deckData && <DeckChart gameData={deckData} />}
    </>
  );
};

export default StatsPage;
